import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  createModalCloseHandler,
  restorePointerEvents,
} from "@/lib/modal-utils";
import {
  Search,
  Filter,
  Plus,
  Download,
  Upload,
  FileSpreadsheet,
  Users,
  Building,
  Calendar,
  Mail,
  Phone,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  UserPlus,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { employeesAPI, departmentsAPI } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import EmployeeForm from "./EmployeeForm";
import EmployeeProfile from "./EmployeeProfile";
import ContractActions from "@/components/employees/ContractActions";
import ContractStats from "@/components/employees/ContractStats";
import ContractBulkActions from "@/components/employees/ContractBulkActions";

const EmployeeManagement = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  // State management
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    department: "all",
    status: "all",
    role: "all",
  });
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddEmployeeForm, setShowAddEmployeeForm] = useState(false);
  const [showEditEmployeeForm, setShowEditEmployeeForm] = useState(false);
  const [showEmployeeProfile, setShowEmployeeProfile] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // table or card
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      setLoading(true);

      // Build params object, filtering out empty values
      const paramsObj = {};

      // Only add search if it's not empty
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        paramsObj.search = debouncedSearchTerm.trim();
      }

      // Only add filters if they have values (not "all")
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all") {
          // Map frontend filter names to backend parameter names
          const paramMap = {
            department: "departement_id",
            status: "statut",
            role: "role",
          };
          const paramName = paramMap[key] || key;
          paramsObj[paramName] = value;
        }
      });

      // Increase limit to show all employees since there is no pagination UI
      paramsObj.limit = 1000;

      const params = new URLSearchParams(paramsObj);
      const response = await employeesAPI.getAll(params);
      setEmployees(response.data.data.employees);
    } catch (error) {
      toast({
        title: t("error"),
        description: t("employees.fetchError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await departmentsAPI.getAll();
      setDepartments(response.data.data.departments || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [debouncedSearchTerm, filters]);

  // Initialize pointer events monitoring to fix modal focus issues
  useEffect(() => {
    // Force restore pointer events on mount
    restorePointerEvents();

    // Set up monitoring for pointer events issues
    const intervalId = setInterval(() => {
      // Only restore if no modal is currently open
      const openDialogs = document.querySelectorAll(
        '[data-state="open"][role="dialog"]'
      );
      if (openDialogs.length === 0) {
        restorePointerEvents();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Handle filters
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle employee actions
  const handleEmployeeAction = async (action, employeeId, data = {}) => {
    try {
      switch (action) {
        case "delete":
          await employeesAPI.delete(employeeId);
          toast({
            title: t("success"),
            description: t("employees.deleteSuccess"),
          });
          break;
        case "toggleStatus":
          const newStatus = data.status === "actif" ? "suspendu" : "actif";
          await employeesAPI.toggleStatus(employeeId, newStatus);
          toast({
            title: t("success"),
            description: t("employees.statusUpdateSuccess"),
          });
          break;
        default:
          break;
      }
      fetchEmployees();
    } catch (error) {
      toast({
        title: t("error"),
        description:
          error.response?.data?.message || t("employees.actionError"),
        variant: "destructive",
      });
    }
  };

  // Handle Excel export
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams();
      if (filters.department && filters.department !== "all")
        params.append("departement_id", filters.department);

      const response = await employeesAPI.export(params);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `employes_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: t("success"),
        description: t("employees.exportSuccess"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("employees.exportError"),
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Handle Excel import
  const handleImport = async (file) => {
    try {
      setImportLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await employeesAPI.import(formData);
      const { success, message, data } = response.data;
      const { success: successCount, total, errors } = data;

      // Show appropriate message based on results
      if (!success) {
        // All imports failed
        toast({
          title: "Erreur d'import",
          description: message,
          variant: "destructive",
        });

        // Show detailed errors in console for debugging
        if (errors.length > 0) {
          console.error("Import errors:", errors);

          // Show first few errors to user
          const errorMessages = errors
            .slice(0, 3)
            .map((err) => `Ligne ${err.row}: ${err.error}`)
            .join("\n");

          toast({
            title: "Détails des erreurs",
            description:
              errorMessages +
              (errors.length > 3
                ? `\n... et ${errors.length - 3} autres erreurs`
                : ""),
            variant: "destructive",
          });
        }
      } else if (errors.length > 0) {
        // Partial success
        toast({
          title: "Import partiel",
          description: message,
          variant: "default",
        });

        // Show errors for partial import
        const errorMessages = errors
          .slice(0, 2)
          .map((err) => `Ligne ${err.row}: ${err.error}`)
          .join("\n");

        toast({
          title: "Erreurs détectées",
          description:
            errorMessages +
            (errors.length > 2
              ? `\n... et ${errors.length - 2} autres erreurs`
              : ""),
          variant: "destructive",
        });
      } else {
        // Complete success
        toast({
          title: "Succès",
          description: message,
        });
      }

      // Refresh employee list if any imports succeeded
      if (successCount > 0) {
        fetchEmployees();
      }
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de l'import du fichier",
        variant: "destructive",
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    try {
      const response = await employeesAPI.downloadTemplate();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_employes.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: t("success"),
        description: t("employees.templateDownloadSuccess"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("employees.templateDownloadError"),
        variant: "destructive",
      });
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (employeeId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedRows(newExpanded);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee, isSelected) => {
    if (isSelected) {
      setSelectedEmployees((prev) => [...prev, employee]);
    } else {
      setSelectedEmployees((prev) =>
        prev.filter((emp) => emp.id !== employee.id)
      );
    }
  };

  // Handle select all employees
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedEmployees([...employees]);
    } else {
      setSelectedEmployees([]);
    }
  };

  // Check if all employees are selected
  const isAllSelected =
    selectedEmployees.length === employees.length && employees.length > 0;

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      actif: { variant: "default", icon: CheckCircle },
      suspendu: { variant: "secondary", icon: AlertCircle },
      conge: { variant: "outline", icon: Calendar },
      demissionne: { variant: "destructive", icon: XCircle },
      licencie: { variant: "destructive", icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.actif;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {t(`employees.status.${status}`)}
      </Badge>
    );
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { variant: "default", icon: UserPlus },
      rh: { variant: "secondary", icon: Users },
      manager: { variant: "outline", icon: Building },
      employe: { variant: "outline", icon: UserPlus },
      stagiaire: { variant: "outline", icon: UserPlus },
    };

    const config = roleConfig[role] || roleConfig.employe;
    const Icon = config.icon;

    return (
      <Badge className="gap-1" variant={config.variant}>
        <Icon className="h-3 w-3" />
        {t(`employees.role.${role}`)}
      </Badge>
    );
  };

  // Get initials
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""
      }`.toUpperCase();
  };

  // Update the edit button click handler to properly load employee data
  const handleEditEmployee = async (employee) => {
    try {
      // Check if employee has valid ID
      if (!employee || !employee.id || employee.id === "undefined") {
        console.error("Invalid employee data:", employee);
        toast({
          title: t("error"),
          description: "Données d'employé invalides",
          variant: "destructive",
        });
        return;
      }

      // Fetch complete employee data including related fields
      const response = await employeesAPI.getById(employee.id);
      const completeEmployee = response.data;

      // Ensure the employee has an ID
      if (!completeEmployee.id) {
        console.error("Fetched employee data missing ID:", completeEmployee);
        // Use the original employee data as fallback
        setSelectedEmployee({ ...employee });
      } else {
        setSelectedEmployee(completeEmployee);
      }

      setShowEditEmployeeForm(true);
    } catch (error) {
      console.error("Error fetching employee details:", error);

      // Show error toast
      toast({
        title: t("error"),
        description:
          error.response?.data?.error ||
          "Erreur lors du chargement des données",
        variant: "destructive",
      });

      // Fallback to using the employee data from the list if available
      if (employee && employee.id) {
        setSelectedEmployee({ ...employee });
        setShowEditEmployeeForm(true);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("employees.title")}
          </h1>
          <p className="text-muted-foreground">{t("employees.description")}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {/* View Mode Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="rounded-r-none"
            >
              <FileText className="h-4 w-4 mr-2" />
              {t("employees.tableView")}
            </Button>
            <Button
              variant={viewMode === "card" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("card")}
              className="rounded-l-none"
            >
              <Users className="h-4 w-4 mr-2" />
              {t("employees.cardView")}
            </Button>
          </div>

          {/* Excel Actions */}
          <div className="flex gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  {t("employees.import")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("employees.importTitle")}</DialogTitle>
                  <DialogDescription>
                    {t("employees.importDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      className="flex-1"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      {t("employees.downloadTemplate")}
                    </Button>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleImport(file);
                        }
                      }}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {t("employees.dragDropOrClick")}
                      </span>
                    </label>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddEmployeeForm(false)}
                  >
                    {t("cancel")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t("employees.export")}
            </Button>

            {/* Add Employee Button */}
            {(user?.role === "admin" || user?.role === "rh") && (
              <Dialog
                open={showAddEmployeeForm}
                onOpenChange={setShowAddEmployeeForm}
              >
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("employees.add")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t("employees.addTitle")}</DialogTitle>
                    <DialogDescription>
                      {t("employees.addDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <EmployeeForm
                    onSuccess={() => {
                      setShowAddEmployeeForm(false);
                      fetchEmployees();
                    }}
                    departments={departments}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Contract Statistics */}
      <ContractStats />

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder={t("employees.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select
                value={filters.department}
                onValueChange={(value) =>
                  handleFilterChange("department", value)
                }
                disabled={departmentsLoading}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue
                    placeholder={
                      departmentsLoading
                        ? t("employees.loadingDepartments")
                        : t("employees.filterByDepartment")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("employees.allDepartments")}
                  </SelectItem>
                  {departments?.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t("employees.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("employees.allStatuses")}
                  </SelectItem>
                  <SelectItem value="actif">
                    {t("employees.status.actif")}
                  </SelectItem>
                  <SelectItem value="suspendu">
                    {t("employees.status.suspendu")}
                  </SelectItem>
                  <SelectItem value="conge">
                    {t("employees.status.conge")}
                  </SelectItem>
                  <SelectItem value="demissionne">
                    {t("employees.status.demissionne")}
                  </SelectItem>
                  <SelectItem value="licencie">
                    {t("employees.status.licencie")}
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.role}
                onValueChange={(value) => handleFilterChange("role", value)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t("employees.filterByRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("employees.allRoles")}</SelectItem>
                  <SelectItem value="admin">
                    {t("employees.role.admin")}
                  </SelectItem>
                  <SelectItem value="rh">{t("employees.role.rh")}</SelectItem>
                  <SelectItem value="manager">
                    {t("employees.role.manager")}
                  </SelectItem>
                  <SelectItem value="employe">
                    {t("employees.role.employe")}
                  </SelectItem>
                  <SelectItem value="stagiaire">
                    {t("employees.role.stagiaire")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contract Bulk Actions */}
          <div className="flex justify-end pt-4 border-t">
            <ContractBulkActions
              selectedEmployees={selectedEmployees}
              onActionComplete={() => {
                setSelectedEmployees([]);
                fetchEmployees();
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Employee Count */}


      {/* Employee List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : viewMode === "table" ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">
                      Immatriculation
                    </th>
                    <th className="text-left p-4 font-medium">
                      {t("employees.employee")}
                    </th>
                    <th className="text-left p-4 font-medium">
                      {t("employees.department")}
                    </th>
                    <th className="text-left p-4 font-medium">
                      {t("employees.generalRole")}
                    </th>
                    <th className="text-left p-4 font-medium">
                      {t("employees.generalStatus")}
                    </th>
                    <th className="text-left p-4 font-medium">
                      {t("employees.hireDate")}
                    </th>
                    <th className="text-center p-4 font-medium bg-blue-50/50 border-l border-r border-blue-100">
                      <div className="flex items-center justify-center gap-2 text-blue-800">
                        <FileText className="h-4 w-4" />
                        Document Contrat
                      </div>
                    </th>
                    <th className="text-right p-4 font-medium">
                      {t("employees.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {employees?.length > 0 ? (
                    employees.map((employee) => (
                      <React.Fragment key={employee.id}>
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border-b hover:bg-muted/50 transition-colors"
                        >
                          <td className="p-4">
                            <div className="text-sm">
                              {employee.immatriculation || "-"}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={employee.photo_profil} />
                                <AvatarFallback>
                                  {getInitials(employee.prenom, employee.nom)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">
                                  {employee.prenom} {employee.nom}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {employee.email}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {employee.numero_employe}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              {employee.departement?.nom ||
                                t("employees.noDepartment")}
                            </div>
                          </td>
                          <td className="p-4">{getRoleBadge(employee.role)}</td>
                          <td className="p-4">
                            {getStatusBadge(employee.statut_employe)}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {new Date(
                                employee.date_embauche
                              ).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="p-4 bg-blue-50/30 border-l border-r border-blue-100">
                            <div className="flex items-center justify-center min-w-[180px]">
                              <ContractActions
                                employee={employee}
                                onContractUpdate={() => fetchEmployees()}
                              />
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleRowExpansion(employee.id)}
                              >
                                {expandedRows.has(employee.id) ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>

                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedEmployee(employee);
                                      setShowEmployeeProfile(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    {t("employees.view")}
                                  </DropdownMenuItem>

                                  {(user?.role === "admin" ||
                                    user?.role === "rh" ||
                                    (user?.role === "manager" &&
                                      user?.departement_id ===
                                      employee.departement_id)) && (
                                      <DropdownMenuItem
                                        onClick={() => {
                                          handleEditEmployee(employee);
                                        }}
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        {t("employees.edit")}
                                      </DropdownMenuItem>
                                    )}

                                  <DropdownMenuSeparator />

                                  {(user?.role === "admin" ||
                                    user?.role === "rh") && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <DropdownMenuItem
                                            onSelect={(e) => e.preventDefault()}
                                            className="text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            {t("employees.delete")}
                                          </DropdownMenuItem>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>
                                              {t("employees.deleteConfirmTitle")}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                              {t(
                                                "employees.deleteConfirmDescription"
                                              )}
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>
                                              {t("cancel")}
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() =>
                                                handleEmployeeAction(
                                                  "delete",
                                                  employee.id
                                                )
                                              }
                                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                              {t("employees.delete")}
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </motion.tr>

                        {/* Expanded Row */}
                        <AnimatePresence>
                          {expandedRows.has(employee.id) && (
                            <motion.tr
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-muted/30"
                            >
                              <td colSpan={8} className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        {t("employees.phone")}:
                                      </span>
                                      <span className="text-sm">
                                        {employee.telephone ||
                                          t("employees.notProvided")}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Mail className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        {t("employees.email")}:
                                      </span>
                                      <span className="text-sm">
                                        {employee.email}
                                      </span>
                                    </div>
                                    {employee.immatriculation && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                          Immatriculation:
                                        </span>
                                        <span className="text-sm">
                                          {employee.immatriculation}
                                        </span>
                                      </div>
                                    )}
                                    {employee.immatriculation && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                          Immatriculation:
                                        </span>
                                        <span className="text-sm">
                                          {employee.immatriculation}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <Building className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        {t("employees.generalContractType")}:
                                      </span>
                                      <span className="text-sm">
                                        {employee.type_contrat
                                          ? t(
                                            `employees.contractType.${employee.type_contrat}`
                                          )
                                          : t("employees.notProvided")}
                                      </span>
                                    </div>
                                    {employee.salaire && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                          {t("employees.salary")}:
                                        </span>
                                        <span className="text-sm">
                                          {employee.salaire.toLocaleString()}{" "}
                                          MAD
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">
                                        {t("employees.generalFamilySituation")}:
                                      </span>
                                      <span className="text-sm">
                                        {employee.situation_familiale
                                          ? t(
                                            `employees.familySituation.${employee.situation_familiale}`
                                          )
                                          : t("employees.notProvided")}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">
                                        {t("employees.children")}:
                                      </span>
                                      <span className="text-sm">
                                        {employee.nombre_enfants}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="p-8 text-center text-muted-foreground"
                      >
                        {t("employees.noEmployees")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {employees?.length > 0 ? (
            employees.map((employee) => (
              <motion.div
                key={employee.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={employee.photo_profil} />
                        <AvatarFallback>
                          {getInitials(employee.prenom, employee.nom)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold">
                          {employee.prenom} {employee.nom}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {employee.numero_employe}
                        </p>
                        {employee.immatriculation && (
                          <p className="text-sm text-muted-foreground">
                            Immatriculation: {employee.immatriculation}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate">
                          {employee.email}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {employee.departement?.nom ||
                            t("employees.noDepartment")}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {new Date(
                            employee.date_embauche
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        {getRoleBadge(employee.role)}
                        {getStatusBadge(employee.statut_employe)}
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedEmployee(employee);
                          setShowEmployeeProfile(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      {(user?.role === "admin" ||
                        user?.role === "rh" ||
                        (user?.role === "manager" &&
                          user?.departement_id ===
                          employee.departement_id)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              handleEditEmployee(employee);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}

                      {(user?.role === "admin" || user?.role === "rh") && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t("employees.deleteConfirmTitle")}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("employees.deleteConfirmDescription")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {t("cancel")}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  handleEmployeeAction("delete", employee.id)
                                }
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("employees.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full flex justify-center items-center h-64">
              <div className="text-center text-muted-foreground">
                {t("employees.noEmployees")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Employee Profile Modal */}
      {selectedEmployee && (
        <Dialog
          open={showEmployeeProfile}
          onOpenChange={createModalCloseHandler((open) => {
            setShowEmployeeProfile(open);
            if (!open) {
              setSelectedEmployee(null);
            }
          })}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <EmployeeProfile
              employee={selectedEmployee}
              onClose={createModalCloseHandler(() => {
                setShowEmployeeProfile(false);
                setSelectedEmployee(null);
              })}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Employee Form Modal */}
      {selectedEmployee && (
        <Dialog
          open={showEditEmployeeForm}
          onOpenChange={createModalCloseHandler((open) => {
            setShowEditEmployeeForm(open);
            if (!open) {
              setSelectedEmployee(null);
            }
          })}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("employees.editTitle")}</DialogTitle>
              <DialogDescription>
                {t("employees.editDescription")}
              </DialogDescription>
            </DialogHeader>
            <EmployeeForm
              employee={selectedEmployee}
              onSuccess={createModalCloseHandler(() => {
                setShowEditEmployeeForm(false);
                setSelectedEmployee(null);
                fetchEmployees();
              })}
              departments={departments}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EmployeeManagement;